import {
  Box,
  Button,
  Card,
  CardContent,
  Switch,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { getFeatures, postFeatures } from "./featuresActions";
import { RootState } from "../../store/store";
import { IFeature } from "../../models/admin/features/features";
import MessageDialog from "../../components/MessageDialog/MessageDialog";
import { styled, alpha } from "@mui/material/styles";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.primary.main,
    0.05
  )}, ${alpha(theme.palette.background.paper, 0.8)})`,
  borderRadius: theme.spacing(1),
  boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: `0 6px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
  },
  "& .feature-content": {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1.5, 2),
    gap: theme.spacing(2),
  },
  "& .feature-children": {
    marginLeft: theme.spacing(4),
    paddingTop: 0,
  },
  "& .MuiSwitch-root": {
    marginLeft: "auto",
  },
  "& .MuiTypography-root": {
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  "& .chevron-icon": {
    color: theme.palette.primary.main,
    transition: "transform 0.2s ease",
  },
}));

const Features = () => {
  const dispatch = useAppDispatch();

  const { features } = useAppSelector((state: RootState) => state.features);
  const { calledReducerType, isError, isSuccess } = useAppSelector(
    (state: RootState) => state.common
  );

  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [messageDialogOpen, setMessageDialogOpen] = useState<boolean>(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [hasChanges, setHasChanges] = useState(false);

  const rootFeatures = useMemo(
    () => features.filter((feature: IFeature) => feature.parentCode === ""),
    [features]
  );

  const handleToggleExpand = (featureCode: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(featureCode)) {
        newSet.delete(featureCode);
      } else {
        newSet.add(featureCode);
      }
      return newSet;
    });
  };

  const handleToggleFeature = (featureCode: string, checked: boolean) => {
    if (checked) {
      // When selecting a feature, also select its parent
      const feature = features.find((f: any) => f.featureCode === featureCode);
      if (feature && feature.parentCode) {
        setSelectedItems((prev) =>
          Array.from(new Set([...prev, featureCode, feature.parentCode]))
        );
      } else {
        setSelectedItems((prev) => Array.from(new Set([...prev, featureCode])));
      }
    } else {
      // When deselecting a feature, also deselect its children
      const childFeatures = getChildFeatures(featureCode);
      setSelectedItems((prev) =>
        prev.filter(
          (item) => !childFeatures.includes(item) && item !== featureCode
        )
      );
    }
    setHasChanges(true);
  };

  const getChildFeatures = useCallback(
    (parentCode: string): string[] => {
      const childFeatures = features.filter(
        (feature: IFeature) => feature.parentCode === parentCode
      );

      if (childFeatures.length === 0) {
        return [];
      }

      let result: string[] = [];
      childFeatures.forEach((feature: IFeature) => {
        result = result.concat(
          feature.featureCode,
          getChildFeatures(feature.featureCode)
        );
      });

      return result;
    },
    [features]
  );

  const handleSave = useCallback(() => {
    if (selectedItems.length > 0) {
      dispatch(
        postFeatures(
          features.map((feature: IFeature) => ({
            ...feature,
            selected: selectedItems.includes(feature.featureCode),
          }))
        )
      );
    } else {
      alert("Please select at least one feature");
    }
    setHasChanges(false);
  }, [selectedItems, dispatch, features]);

  const handleMessageDialogClose = useCallback(() => {
    setMessageDialogOpen(false);
    dispatch(getFeatures());
  }, [dispatch]);

  useEffect(() => {
    const selectedItems = features
      .filter((feature: IFeature) => feature.selected)
      .map((feature: IFeature) => feature.featureCode);
    setSelectedItems(selectedItems);
  }, [features]);

  useEffect(() => {
    if (
      (isSuccess || isError) &&
      calledReducerType === "features/postFeatures"
    ) {
      setMessageDialogOpen(true);
    }
  }, [isError, isSuccess, calledReducerType]);

  const renderFeatureCard = (feature: IFeature) => {
    const hasChildren = features.some(
      (f: any) => f.parentCode === feature.featureCode
    );
    const isExpanded = expandedItems.has(feature.featureCode);
    const childFeatures = features.filter(
      (f: any) => f.parentCode === feature.featureCode
    );

    return (
      <StyledCard
        key={feature.featureCode}
        id={`features_card_${feature.featureCode}`}
        data-test-id={`features_card_${feature.featureCode}`}
        aria-label={`${feature.featureLabel} Feature Card`}
      >
        <div
          className="feature-content"
          id={`features_content_${feature.featureCode}`}
          data-test-id={`features_content_${feature.featureCode}`}
          aria-label={`${feature.featureLabel} Feature Content`}
        >
          {hasChildren && (
            <Box
              component="span"
              onClick={() => handleToggleExpand(feature.featureCode)}
              sx={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                "&:hover": {
                  "& .chevron-icon": {
                    color: (theme) => theme.palette.primary.dark,
                  },
                },
              }}
              id={`features_expand_${feature.featureCode}`}
              data-test-id={`features_expand_${feature.featureCode}`}
              aria-label={`Expand ${feature.featureLabel} Feature`}
            >
              {isExpanded ? (
                <KeyboardArrowDownIcon className="chevron-icon" />
              ) : (
                <ChevronRightIcon className="chevron-icon" />
              )}
            </Box>
          )}
          {!hasChildren && <Box sx={{ width: 24 }} />}
          <Typography
            sx={{ flex: 1 }}
            id={`features_label_${feature.featureCode}`}
            data-test-id={`features_label_${feature.featureCode}`}
            aria-label={`${feature.featureLabel} Feature Label`}
          >
            {feature.featureLabel}
          </Typography>
          <Switch
            checked={selectedItems.includes(feature.featureCode)}
            onChange={(e) =>
              handleToggleFeature(feature.featureCode, e.target.checked)
            }
            color="primary"
            id={`features_switch_${feature.featureCode}`}
            data-test-id={`features_switch_${feature.featureCode}`}
            aria-label={`Toggle ${feature.featureLabel} Feature`}
          />
        </div>
        {hasChildren && isExpanded && (
          <Box
            sx={{
              maxHeight: "calc(80vh - 100px)",
              overflowY: "auto",
              borderLeft: "1px dashed",
              borderColor: "divider",
              ml: 4,
            }}
            id={`features_children_${feature.featureCode}`}
            data-test-id={`features_children_${feature.featureCode}`}
            aria-label={`${feature.featureLabel} Child Features`}
          >
            <CardContent sx={{ pt: 0 }}>
              {childFeatures.map((childFeature: any) =>
                renderFeatureCard(childFeature)
              )}
            </CardContent>
          </Box>
        )}
      </StyledCard>
    );
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          height: "84vh",
          padding: 2,
        }}
        id="features_container"
        data-test-id="features_container"
        aria-label="Features Management Container"
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "end",
            alignItems: "center",
            mb: 2,
          }}
          id="features_header"
          data-test-id="features_header"
          aria-label="Features Header"
        >
          {/* <Typography
            variant="h4"
            sx={{
              background: (theme) =>
                `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontWeight: "bold",
            }}
          >
            Feature Management
          </Typography> */}
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!hasChanges}
            sx={{
              textTransform: "none",
              px: 3,
              borderRadius: "4px",
            }}
            id="features_save_button"
            data-test-id="features_save_button"
            aria-label="Save Features"
          >
            Save
          </Button>
        </Box>
        <Box
          sx={{ flex: 1 }}
          id="features_list"
          data-test-id="features_list"
          aria-label="Features List"
        >
          {rootFeatures.map((feature: any) => renderFeatureCard(feature))}
        </Box>
      </Box>
      <MessageDialog
        open={messageDialogOpen}
        handleClose={handleMessageDialogClose}
      />
    </>
  );
};

export default Features;
